import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  completeEmbeddedAiToolPayment,
  consumeAiToolCompletedPayment,
} from '../utils/aiTool/payment'

export function useAiToolEmbeddedPaymentRestore(toolType, onRestore) {
  useEffect(() => {
    const completed = consumeAiToolCompletedPayment(toolType)
    if (completed) {
      onRestore(completed)
    }
  }, [toolType, onRestore])
}

/**
 * Handles backend redirect directly to a tool page with ?paymentSuccess=1
 * (when it skips /ai-tool/payment/return).
 */
export function useAiToolEmbeddedPaymentCompletion(
  toolType,
  { onComplete, onError },
) {
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get('paymentSuccess') !== '1') return

    let cancelled = false
    const paymentId = searchParams.get('paymentId')

    const finish = async () => {
      const completed = consumeAiToolCompletedPayment(toolType, paymentId)
      if (completed) {
        onComplete?.(completed)
        clearSuccessParam(setSearchParams, searchParams)
        return
      }

      if (!paymentId) {
        onError?.('Payment session expired. Please try again.')
        clearSuccessParam(setSearchParams, searchParams)
        return
      }

      try {
        const result = await completeEmbeddedAiToolPayment({
          paymentId,
          toolType,
        })
        if (cancelled) return

        onComplete?.(result)
      } catch (error) {
        if (!cancelled) {
          onError?.(
            error?.response?.data?.message ||
              error.message ||
              'Payment processing failed',
          )
        }
      } finally {
        if (!cancelled) {
          clearSuccessParam(setSearchParams, searchParams)
        }
      }
    }

    finish()

    return () => {
      cancelled = true
    }
  }, [toolType, onComplete, onError, searchParams, setSearchParams])
}

function clearSuccessParam(setSearchParams, searchParams) {
  const nextParams = new URLSearchParams(searchParams)
  nextParams.delete('paymentSuccess')
  setSearchParams(nextParams, { replace: true })
}
